import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from 'react'

import {
  Botao,
  DataInputUnform,
  FormRef,
  FormUnform,
  IconeCarregando,
  ModalBase,
  SelectAsync,
  SelectUnform
} from 'src/componentes'
import { OpcaoSelect } from 'src/componentes/select-async/tipos'
import { Api } from 'src/servicos'
import {
  AlunoSituacao,
  SetorRescisao,
  SetorRescisaoPorExtenso
} from 'src/tipos'

import {
  AcoesModal,
  AvisoBloqueio,
  AvisoVerificando,
  CampoAluno,
  CorpoModal,
  TituloModal
} from './styles'

export interface ModalNovoPedidoRescisaoRef {
  abrir: () => void
  fechar: () => void
}

interface ModalNovoPedidoRescisaoProps {
  backdrop?: boolean
  aoCriar: (id: string) => void
}

interface FormNovoPedido {
  setorResponsavel?: string
  dataPrimeiroContato?: string
}

const ModalNovoPedidoRescisaoComponente: React.ForwardRefRenderFunction<
  ModalNovoPedidoRescisaoRef,
  ModalNovoPedidoRescisaoProps
> = ({ backdrop, aoCriar }, ref) => {
  const formRef = useRef<FormRef>(null)
  const [aberta, definirAberta] = useState(false)
  const [carregando, definirCarregando] = useState(false)
  const [alunoSelecionado, definirAlunoSelecionado] = useState<OpcaoSelect>(
    null
  )
  const [motivoBloqueio, definirMotivoBloqueio] = useState<string>(null)
  const [verificandoElegibilidade, definirVerificandoElegibilidade] = useState(
    false
  )

  const fechar = () => {
    definirAberta(false)
    definirAlunoSelecionado(null)
    definirMotivoBloqueio(null)
    definirCarregando(false)
    definirVerificandoElegibilidade(false)
  }

  const abrir = () => definirAberta(true)

  useImperativeHandle(ref, () => ({ abrir, fechar }))

  // Busca REAL de alunos (reusa o endpoint existente de listagem de alunos).
  const buscarAlunos = async (texto: string): Promise<OpcaoSelect[]> => {
    if (!texto || texto.length < 3) return []

    const dados = await Api.RequisitarListaAlunos(
      texto,
      null,
      null,
      'Todas' as keyof typeof AlunoSituacao,
      null,
      null,
      'Todas',
      null,
      null,
      1,
      10
    )

    return (dados?.registros ?? []).map(aluno => ({
      id: aluno.id,
      texto: `${aluno.nomeApresentacao} — ${aluno.email}`
    }))
  }

  const aoSelecionarAluno = async (opcao: OpcaoSelect) => {
    definirAlunoSelecionado(opcao)
    definirMotivoBloqueio(null)

    if (!opcao) return

    try {
      definirVerificandoElegibilidade(true)
      const elegibilidade = await Api.VerificarElegibilidadeRescisao(opcao.id)
      if (!elegibilidade.podeAbrir) {
        definirMotivoBloqueio(
          elegibilidade.motivoBloqueio ??
            'Este aluno não está elegível para rescisão.'
        )
      }
    } finally {
      definirVerificandoElegibilidade(false)
    }
  }

  const criar = async (dados: FormNovoPedido) => {
    if (!alunoSelecionado) return

    try {
      definirCarregando(true)
      const resultado = await Api.AbrirProtocoloRescisao(
        alunoSelecionado.id,
        dados.setorResponsavel,
        dados.dataPrimeiroContato
      )
      aoCriar(resultado.id)
      fechar()
    } catch {
      // Erros de validação (ex.: protocolo duplicado) já são exibidos via
      // interceptor do gatewayApi. Apenas garantimos o estado consistente.
      definirCarregando(false)
    }
  }

  // `carregando` fica fora daqui de propósito: o CSS de :disabled esconde o loader do Botao
  // (`& > i { visibility: hidden }`), então marcar disabled durante o envio matava o spinner.
  // O próprio estado `carregando` já bloqueia o clique (pointer-events: none).
  const submitDesabilitado =
    !alunoSelecionado || !!motivoBloqueio || verificandoElegibilidade

  return (
    <ModalBase id="modal-novo-pedido-rescisao" aberta={aberta} backdrop={backdrop}>
      <CorpoModal>
        <TituloModal>Novo pedido de rescisão</TituloModal>
        <p>
          Selecione o aluno para abrir o protocolo. O curso, o motivo e os
          demais dados serão preenchidos pelo próprio aluno no formulário
          enviado por e-mail.
        </p>

        <CampoAluno>
          <SelectAsync
            id="slct_aluno_rescisao"
            label="Aluno"
            placeholder="Digite o nome ou e-mail do aluno"
            buscarPorTexto={buscarAlunos}
            defineValor={aoSelecionarAluno}
            tamanho="S"
          />
        </CampoAluno>

        {verificandoElegibilidade && (
          <AvisoVerificando>
            <i>{IconeCarregando}</i>
            Verificando se o aluno pode abrir um pedido de rescisão...
          </AvisoVerificando>
        )}

        {motivoBloqueio && <AvisoBloqueio>{motivoBloqueio}</AvisoBloqueio>}

        <FormUnform ref={formRef} acaoSucesso={criar}>
          <SelectUnform
            id="slct_setor_novo_pedido"
            name="setorResponsavel"
            label="Setor (opcional)"
            iconeFechar
            opcoes={[
              {
                id: SetorRescisao.Academico,
                texto: SetorRescisaoPorExtenso.Academico
              },
              {
                id: SetorRescisao.Financeiro,
                texto: SetorRescisaoPorExtenso.Financeiro
              }
            ]}
          />

          <DataInputUnform
            id="dt_primeiro_contato_novo_pedido"
            name="dataPrimeiroContato"
            label="Data do 1º contato (opcional)"
          />

          <AcoesModal>
            <Botao
              texto="Cancelar"
              tema="Secundario"
              type="button"
              disabled={carregando}
              onClick={fechar}
            />
            <Botao
              texto="Abrir protocolo"
              type="submit"
              carregando={carregando}
              disabled={submitDesabilitado}
            />
          </AcoesModal>
        </FormUnform>
      </CorpoModal>
    </ModalBase>
  )
}

export const ModalNovoPedidoRescisao = forwardRef(
  ModalNovoPedidoRescisaoComponente
)
